import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    toggleCommentReaction,
    createComment,
    updateComment,
    deleteComment,
} from '../../api/reviews';
import { ThumbUpIcon, ThumbDownIcon } from './ReactionBar';
import logo from '../../assets/STAMP-LOGO.webp';

export function displayName(user) {
    if (!user) return 'Anonymous';
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return name || user.username || 'Anonymous';
}

export default function CommentCard({ eventPk, comment, requireLogin }) {
    // 대댓글의 reply는 같은 스레드(top-level parent)에 달리고 원 작성자를 멘션한다.
    const isReply = !!comment.parent;
    const threadParentPk = isReply ? comment.parent : comment.pk;

    const [replyOpen, setReplyOpen] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [editing, setEditing] = useState(false);
    const [editContent, setEditContent] = useState(comment.content);

    const queryClient = useQueryClient();
    const invalidateComments = () =>
        queryClient.invalidateQueries({ queryKey: ['comments', eventPk] });

    const reactionMutation = useMutation({
        mutationFn: (reaction) =>
            toggleCommentReaction(eventPk, comment.pk, reaction),
        onSuccess: invalidateComments,
        onError: () => alert('Failed to update reaction. Please try again.'),
    });

    const replyMutation = useMutation({
        mutationFn: () =>
            createComment(eventPk, {
                content: replyContent,
                parent: threadParentPk,
                mentionedUser: isReply ? comment.created_by?.pk : null,
            }),
        onSuccess: () => {
            setReplyContent('');
            setReplyOpen(false);
            invalidateComments();
        },
        onError: () => alert('Failed to post reply. Please try again.'),
    });

    const updateMutation = useMutation({
        mutationFn: () => updateComment(eventPk, comment.pk, editContent),
        onSuccess: () => {
            setEditing(false);
            invalidateComments();
        },
        onError: () => alert('Failed to update comment. Please try again.'),
    });

    const deleteMutation = useMutation({
        mutationFn: () => deleteComment(eventPk, comment.pk),
        onSuccess: invalidateComments,
        onError: () => alert('Failed to delete comment. Please try again.'),
    });

    const handleReaction = (reaction) => {
        if (!requireLogin('react')) return;
        reactionMutation.mutate(reaction);
    };

    const handleReplyToggle = () => {
        if (!requireLogin('reply')) return;
        setReplyOpen((open) => !open);
    };

    const handleReplySubmit = () => {
        if (!replyContent.trim()) return;
        if (replyMutation.isPending) return;
        replyMutation.mutate();
    };

    const handleDelete = () => {
        if (window.confirm('Delete this comment?')) {
            deleteMutation.mutate();
        }
    };

    return (
        <div
            className={`comment-card ${isReply ? 'is-reply' : ''} ${
                comment.mentioned_user ? 'is-nested-reply' : ''
            }`}
        >
            <img
                className="comment-avatar"
                src={comment.created_by?.profile_photo || logo}
                alt={displayName(comment.created_by)}
            />
            <div className="comment-body">
                <div className="comment-meta">
                    <span className="comment-author">
                        {displayName(comment.created_by)}
                    </span>
                    <span className="comment-date">
                        {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                </div>

                {editing ? (
                    <div>
                        <textarea
                            className="comment-edit-area"
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                        />
                        <div className="comment-actions">
                            <button
                                className="comment-action-btn"
                                onClick={() => updateMutation.mutate()}
                                disabled={
                                    updateMutation.isPending ||
                                    !editContent.trim()
                                }
                            >
                                Save
                            </button>
                            <button
                                className="comment-action-btn"
                                onClick={() => {
                                    setEditing(false);
                                    setEditContent(comment.content);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <p className="comment-content">
                        {comment.mentioned_user && (
                            <span className="comment-mention">
                                @{displayName(comment.mentioned_user)}
                            </span>
                        )}
                        {comment.content}
                    </p>
                )}

                <div className="comment-actions">
                    <button
                        className={`comment-action-btn ${comment.my_reaction === 'like' ? 'active' : ''}`}
                        aria-label="Like"
                        onClick={() => handleReaction('like')}
                        disabled={reactionMutation.isPending}
                    >
                        <ThumbUpIcon />
                        {comment.likes_count ?? 0}
                    </button>
                    <button
                        className={`comment-action-btn ${comment.my_reaction === 'dislike' ? 'active' : ''}`}
                        aria-label="Dislike"
                        onClick={() => handleReaction('dislike')}
                        disabled={reactionMutation.isPending}
                    >
                        <ThumbDownIcon />
                    </button>
                    <button
                        className="comment-action-btn"
                        onClick={handleReplyToggle}
                    >
                        reply
                    </button>
                    {comment.is_owner && !editing && (
                        <>
                            <button
                                className="comment-action-btn"
                                onClick={() => {
                                    setEditContent(comment.content);
                                    setEditing(true);
                                }}
                            >
                                Edit
                            </button>
                            <button
                                className="comment-action-btn"
                                onClick={handleDelete}
                                disabled={deleteMutation.isPending}
                            >
                                Delete
                            </button>
                        </>
                    )}
                </div>

                {replyOpen && (
                    <div className="reply-form">
                        <input
                            placeholder={
                                isReply
                                    ? `Reply to @${displayName(comment.created_by)}...`
                                    : 'add a reply...'
                            }
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleReplySubmit();
                            }}
                        />
                        <button
                            className="comment-submit-btn"
                            onClick={handleReplySubmit}
                            disabled={
                                replyMutation.isPending || !replyContent.trim()
                            }
                        >
                            Reply
                        </button>
                    </div>
                )}

                {!isReply && comment.replies?.length > 0 && (
                    <div className="comment-replies">
                        {comment.replies.map((reply) => (
                            <CommentCard
                                key={reply.pk}
                                eventPk={eventPk}
                                comment={{ ...reply, parent: comment.pk }}
                                requireLogin={requireLogin}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
